import { useRef, useState } from 'react'
import {
  AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, FileText,
  Loader2, Paperclip, Plus, Trash2, Upload, X,
} from 'lucide-react'
import { Button, Chip, Drawer } from '@/components/ui'
import { useQueryClient } from '@tanstack/react-query'
import { ORDER_TYPE_LABELS, type OrderType } from '../api/types'
import { useCreateShipToContact, useShipToContacts } from '../api/queries'
import {
  useUvivaShipmentExtractionQueue,
  type ExtractionQueueItem,
} from '@/shell/services/uviva-shipment-queue'

const BASE = '/api/v1/uviva'

// ── Types ─────────────────────────────────────────────────────────────────────

interface ExtractedLine {
  product_code: string
  product_name: string
  lot_serial: string
  qty_to_ship: string
  uom: string
  sell_by: string
  coa_file: File | null
}

interface Extracted {
  shipment_ref: string
  po_ref: string
  order_ref: string
  planned_delivery_date: string
  lines: ExtractedLine[]
  extraction_notes: string
}

/** One shipment sheet in the batch, from successful extraction through submit. */
interface ReviewEntry {
  clientId: string
  file: File
  form: Extracted
  billOfLading: File | null
  invoice: File | null
  status: 'reviewing' | 'submitting' | 'submitted'
  submitError?: string
}

function emptyLine(): ExtractedLine {
  return {
    product_code: '', product_name: '', lot_serial: '',
    qty_to_ship: '', uom: 'kg', sell_by: '', coa_file: null,
  }
}

function fromExtracted(raw: Record<string, unknown>): Extracted {
  const rawLines = Array.isArray(raw.lines) ? raw.lines : []
  return {
    shipment_ref: String(raw.shipment_ref ?? ''),
    po_ref: String(raw.po_ref ?? ''),
    order_ref: String(raw.order_ref ?? ''),
    planned_delivery_date: String(raw.planned_delivery_date ?? ''),
    extraction_notes: String(raw.extraction_notes ?? ''),
    lines: rawLines.length > 0
      ? rawLines.map((l: Record<string, unknown>) => ({
          product_code: String(l.product_code ?? ''),
          product_name: String(l.product_name ?? ''),
          lot_serial: String(l.lot_serial ?? ''),
          qty_to_ship: l.qty_to_ship != null ? String(l.qty_to_ship) : '',
          uom: String(l.uom ?? 'kg'),
          sell_by: String(l.sell_by ?? ''),
          coa_file: null,
        }))
      : [emptyLine()],
  }
}

/** Builds a review entry from a completed extraction. `poRefDefault` is
 *  whatever the user typed in step 1 before extraction ran — used as a
 *  fallback only when the sheet itself didn't yield a PO reference. */
function buildEntry(item: ExtractionQueueItem, poRefDefault: string): ReviewEntry {
  const next = fromExtracted(item.extracted ?? {})
  return {
    clientId: item.clientId,
    file: item.file,
    form: { ...next, po_ref: next.po_ref.trim() ? next.po_ref : poRefDefault },
    billOfLading: null,
    invoice: null,
    status: 'reviewing',
  }
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface NewSubmissionDrawerProps {
  open: boolean
  onClose: () => void
}

// ── Component ─────────────────────────────────────────────────────────────────

export function NewSubmissionDrawer({ open, onClose }: NewSubmissionDrawerProps) {
  const qc = useQueryClient()
  const [phase, setPhase] = useState<'upload' | 'extracting' | 'review'>('upload')
  const [error, setError] = useState<string | null>(null)

  // Order classification — decided up front, shared across every shipment
  // sheet dropped into this batch.
  const [orderType, setOrderType] = useState<OrderType>('spi_order')
  const [shipToId, setShipToId] = useState<number | null>(null)
  const [addingContact, setAddingContact] = useState(false)
  const [newContact, setNewContact] = useState({
    name: '', street: '', city: '', zip: '', country_code: '',
  })
  const shipToContacts = useShipToContacts(orderType === 'uviva_direct')
  const createContact = useCreateShipToContact()
  // Typed in step 1 before extraction — used only as a per-entry fallback
  // when a given sheet doesn't carry its own PO reference.
  const [poRefDefault, setPoRefDefault] = useState('')

  // Upload phase — files staged before "Extract data" fires the queue.
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const shipmentRef = useRef<HTMLInputElement>(null)

  // Extraction queue — 3-wide concurrency across every staged file.
  const {
    items: queueItems, enqueue, removeItem: removeQueueItem, reset: resetQueue,
    busy: extractingBusy,
  } = useUvivaShipmentExtractionQueue({
    onItemExtracted: (item) => {
      setEntries((prev) => [...prev, buildEntry(item, poRefDefault)])
    },
    // Fires once the queue fully drains (every item extracted or errored,
    // nothing in flight). Hands off from the bulk "extracting" view into
    // per-sheet review — only relevant the first time, since a later retry
    // from inside review shouldn't yank the user back out of review.
    onSettled: () => {
      setPhase((p) => (p === 'extracting' ? 'review' : p))
    },
  })

  // Review entries — one per successfully-extracted shipment sheet.
  const [entries, setEntries] = useState<ReviewEntry[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const current = entries[currentIndex]

  function resetAll() {
    setPhase('upload')
    setError(null)
    setOrderType('spi_order')
    setShipToId(null)
    setAddingContact(false)
    setNewContact({ name: '', street: '', city: '', zip: '', country_code: '' })
    setPoRefDefault('')
    setPendingFiles([])
    resetQueue()
    setEntries([])
    setCurrentIndex(0)
  }

  function handleClose() {
    const unresolved = entries.filter((e) => e.status !== 'submitted').length
    if (unresolved > 0 || extractingBusy) {
      const noun = unresolved === 1 ? 'shipment sheet' : 'shipment sheets'
      const verb = unresolved === 1 ? "hasn't" : "haven't"
      const ok = window.confirm(
        unresolved > 0
          ? `${unresolved} ${noun} in this batch ${verb} been submitted yet. Close anyway?`
          : 'Extraction is still running. Close anyway?',
      )
      if (!ok) return
    }
    resetAll()
    onClose()
  }

  // ── Upload phase ────────────────────────────────────────────────────────────

  function addFiles(fileList: FileList | File[] | null | undefined) {
    if (!fileList) return
    const list = Array.from(fileList)
    if (list.length === 0) return
    setPendingFiles((prev) => [...prev, ...list])
  }

  function removePendingFile(idx: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  function onDropFiles(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    addFiles(e.dataTransfer?.files)
  }

  function onDragOverFiles(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
  }

  function handleExtract() {
    if (pendingFiles.length === 0) return
    setError(null)
    enqueue(pendingFiles)
    setPendingFiles([])
    setPhase('extracting')
  }

  function retryItem(item: ExtractionQueueItem) {
    removeQueueItem(item.clientId)
    enqueue([item.file])
  }

  // ── Entry (per-shipment-sheet) helpers ──────────────────────────────────────

  function updateEntry(clientId: string, patch: Partial<ReviewEntry>) {
    setEntries((prev) => prev.map((e) => (e.clientId === clientId ? { ...e, ...patch } : e)))
  }

  function setEntryField<K extends keyof Extracted>(clientId: string, key: K, value: Extracted[K]) {
    setEntries((prev) => prev.map((e) =>
      e.clientId === clientId ? { ...e, form: { ...e.form, [key]: value } } : e,
    ))
  }

  function setEntryLineField(
    clientId: string, idx: number, field: keyof ExtractedLine, value: string | File | null,
  ) {
    setEntries((prev) => prev.map((e) => e.clientId === clientId
      ? { ...e, form: { ...e.form, lines: e.form.lines.map((l, i) => i === idx ? { ...l, [field]: value } : l) } }
      : e,
    ))
  }

  function addEntryLine(clientId: string) {
    setEntries((prev) => prev.map((e) =>
      e.clientId === clientId ? { ...e, form: { ...e.form, lines: [...e.form.lines, emptyLine()] } } : e,
    ))
  }

  function removeEntryLine(clientId: string, idx: number) {
    setEntries((prev) => prev.map((e) =>
      e.clientId === clientId ? { ...e, form: { ...e.form, lines: e.form.lines.filter((_, i) => i !== idx) } } : e,
    ))
  }

  function removeEntry(clientId: string) {
    setEntries((prev) => {
      const next = prev.filter((e) => e.clientId !== clientId)
      setCurrentIndex((idx) => Math.min(idx, Math.max(0, next.length - 1)))
      return next
    })
  }

  /** After a successful submit, jump to the next not-yet-submitted entry
   *  (wrapping around) so the user can just keep hitting Submit. */
  function advanceToNextReviewing(justSubmittedId: string) {
    const idx = entries.findIndex((e) => e.clientId === justSubmittedId)
    if (idx === -1) return
    const rotated = [...entries.slice(idx + 1), ...entries.slice(0, idx)]
    const next = rotated.find((e) => e.status === 'reviewing')
    if (next) setCurrentIndex(entries.findIndex((e) => e.clientId === next.clientId))
  }

  function goPrev() { setCurrentIndex((i) => Math.max(0, i - 1)) }
  function goNext() { setCurrentIndex((i) => Math.min(entries.length - 1, i + 1)) }

  // ── Validation ──────────────────────────────────────────────────────────────

  function isOrderTypeValid(form: Extracted): boolean {
    return orderType === 'uviva_direct'
      ? shipToId != null
      : orderType === 'uvwh_intake'
      ? true
      : form.po_ref.trim().length > 0
  }

  function isEntryValid(entry: ReviewEntry): boolean {
    return entry.form.shipment_ref.trim().length > 0
      && isOrderTypeValid(entry.form)
      && entry.form.lines.length > 0
      && entry.form.lines.every(
        (l) => l.product_code.trim() && l.lot_serial.trim() && parseFloat(l.qty_to_ship) > 0,
      )
  }

  // ── Submit (per entry) ───────────────────────────────────────────────────────

  async function handleSubmitEntry(entry: ReviewEntry) {
    updateEntry(entry.clientId, { status: 'submitting', submitError: undefined })
    try {
      const isDirect = orderType === 'uviva_direct'
      const hasPo = orderType === 'spi_order'
      const payload = {
        shipment_ref: entry.form.shipment_ref.trim(),
        po_ref: hasPo ? entry.form.po_ref.trim() : null,
        order_ref: entry.form.order_ref.trim() || null,
        planned_delivery_date: entry.form.planned_delivery_date || null,
        order_type: orderType,
        ship_to_partner_id: isDirect ? shipToId : null,
        lines: entry.form.lines.map((l) => ({
          product_code: l.product_code.trim(),
          product_name: l.product_name.trim() || null,
          lot_serial: l.lot_serial.trim(),
          qty_to_ship: parseFloat(l.qty_to_ship),
          uom: l.uom || null,
          sell_by: l.sell_by || null,
          // CoA optional on Direct orders (stock already received).
          coa: l.coa_file
            ? { lot: l.lot_serial.trim(), attachment_ref: l.coa_file.name }
            : null,
        })),
      }

      const fd = new FormData()
      fd.append('payload', JSON.stringify(payload))
      fd.append('shipment_sheet', entry.file)
      if (entry.billOfLading) fd.append('bill_of_lading', entry.billOfLading)
      if (entry.invoice) fd.append('invoice', entry.invoice)
      entry.form.lines.forEach((l) => { if (l.coa_file) fd.append('coa', l.coa_file) })

      const res = await fetch(`${BASE}/submissions`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body.detail as string | undefined) ?? `Submit failed (${res.status})`)
      }

      qc.invalidateQueries({ queryKey: ['uviva'] })
      updateEntry(entry.clientId, { status: 'submitted' })
      advanceToNextReviewing(entry.clientId)
    } catch (e) {
      updateEntry(entry.clientId, {
        status: 'reviewing',
        submitError: e instanceof Error ? e.message : 'Submission failed. Please try again.',
      })
    }
  }

  async function handleAddContact() {
    const name = newContact.name.trim()
    if (!name) return
    try {
      const created = await createContact.mutateAsync({
        name,
        street: newContact.street.trim() || undefined,
        city: newContact.city.trim() || undefined,
        zip: newContact.zip.trim() || undefined,
        country_code: newContact.country_code.trim() || undefined,
      })
      setShipToId(created.id)
      setAddingContact(false)
      setNewContact({ name: '', street: '', city: '', zip: '', country_code: '' })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create contact')
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const stepBadge = (n: number, label: string) => (
    <div className="flex items-center gap-1.5">
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium ${
          (phase === 'upload' && n === 1) || (phase === 'extracting' && n === 1) || (phase === 'review' && n === 2)
            ? 'bg-accent text-on-accent'
            : (phase === 'review' && n === 1)
            ? 'bg-success text-on-success'
            : 'bg-surface-sunk text-tertiary'
        }`}
      >
        {n}
      </span>
      <span className={`text-xs ${
        (phase === 'upload' && n === 1) || (phase === 'review' && n === 2)
          ? 'text-primary'
          : 'text-tertiary'
      }`}>
        {label}
      </span>
    </div>
  )

  const submittedCount = entries.filter((e) => e.status === 'submitted').length
  const queueIssues = queueItems.filter(
    (it) => it.status === 'error' || it.status === 'extracting' || it.status === 'queued',
  )

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => { if (!o) handleClose() }}
      size="wide"
      title="New Submission"
      meta={
        <div className="flex items-center gap-3">
          {stepBadge(1, 'Details')}
          <span className="text-tertiary text-xs">·</span>
          {stepBadge(2, 'Review & Submit')}
        </div>
      }
      headerActions={
        phase === 'review' && entries.length > 0 ? (
          <Chip size="xs" variant={submittedCount === entries.length ? 'success' : 'info'}>
            {submittedCount} / {entries.length} submitted
          </Chip>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        {error && (
          <p className="rounded border border-danger-border bg-danger-subtle px-3 py-2 text-sm text-danger">
            {error}
          </p>
        )}

        {/* ── Phase 1: Details ─────────────────────────────────────────────── */}
        {(phase === 'upload' || phase === 'extracting') && (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <p className="text-sm text-secondary">
                Choose the order type, then attach one or more shipment sheets —
                we'll read the data automatically. You'll review each one before submitting.
              </p>
            </div>

            {/* Order type */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-primary">Order type *</label>
              <div className="flex gap-2">
                {(['spi_order', 'uvwh_intake', 'uviva_direct'] as OrderType[]).map((ot) => (
                  <button
                    key={ot}
                    type="button"
                    disabled={phase === 'extracting'}
                    onClick={() => setOrderType(ot)}
                    className={`flex-1 rounded border px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                      orderType === ot
                        ? 'border-accent bg-accent-subtle text-primary'
                        : 'border-border bg-surface text-secondary hover:bg-surface-sunk'
                    }`}
                  >
                    {ORDER_TYPE_LABELS[ot]}
                  </button>
                ))}
              </div>
              <p className="text-xs text-tertiary">
                {orderType === 'spi_order'
                  ? 'Reconciles to an SPI purchase order — lands wherever that PO receives (your warehouse or SPI’s own).'
                  : orderType === 'uvwh_intake'
                  ? 'No SPI purchase order — received directly into your held UVWH inventory.'
                  : 'A Uviva service order SPI ships out of the Uviva warehouse on your behalf.'}
              </p>
            </div>

            {/* Order-type-specific field */}
            {orderType === 'spi_order' && (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-primary">PO Reference</label>
                <input
                  type="text"
                  value={poRefDefault}
                  onChange={(e) => setPoRefDefault(e.target.value)}
                  disabled={phase === 'extracting'}
                  placeholder="Applied to any sheet that doesn't have its own PO"
                  className="rounded border border-border bg-surface px-3 py-2 text-sm text-primary outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
                />
                <p className="text-xs text-tertiary">
                  Each sheet is required to have a PO reference before it can be
                  submitted — set individually in review if sheets use different POs.
                </p>
              </div>
            )}
            {orderType === 'uviva_direct' && (
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-primary">Ship to *</label>
                  <div className="flex gap-2">
                    <select
                      value={shipToId ?? ''}
                      onChange={(e) => setShipToId(e.target.value ? Number(e.target.value) : null)}
                      disabled={phase === 'extracting'}
                      className="flex-1 rounded border border-border bg-surface px-3 py-2 text-sm text-primary outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
                    >
                      <option value="">Select a delivery contact…</option>
                      {(shipToContacts.data?.contacts ?? []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}{c.city ? ` — ${c.city}` : ''}
                        </option>
                      ))}
                    </select>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={phase === 'extracting'}
                      onClick={() => setAddingContact((v) => !v)}
                      title="Add a new delivery contact"
                    >
                      <Plus size={14} /> New
                    </Button>
                  </div>
                </div>

                {/* Add a new delivery contact */}
                {addingContact && (
                  <div className="flex flex-col gap-2 rounded border border-border-subtle bg-surface-sunk p-3">
                    <span className="text-sm font-medium text-primary">New delivery contact</span>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={newContact.name}
                        onChange={(e) => setNewContact((p) => ({ ...p, name: e.target.value }))}
                        placeholder="Name *"
                        className="col-span-2 rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
                      />
                      <input
                        type="text"
                        value={newContact.street}
                        onChange={(e) => setNewContact((p) => ({ ...p, street: e.target.value }))}
                        placeholder="Street *"
                        className="col-span-2 rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
                      />
                      <input
                        type="text"
                        value={newContact.city}
                        onChange={(e) => setNewContact((p) => ({ ...p, city: e.target.value }))}
                        placeholder="City *"
                        className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
                      />
                      <input
                        type="text"
                        value={newContact.zip}
                        onChange={(e) => setNewContact((p) => ({ ...p, zip: e.target.value }))}
                        placeholder="ZIP / Postal code *"
                        className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
                      />
                      <input
                        type="text"
                        value={newContact.country_code}
                        onChange={(e) => setNewContact((p) => ({ ...p, country_code: e.target.value.toUpperCase() }))}
                        placeholder="Country code (e.g. US) *"
                        maxLength={2}
                        className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setAddingContact(false)}>Cancel</Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddContact}
                        disabled={
                          !newContact.name.trim()
                          || !newContact.street.trim()
                          || !newContact.city.trim()
                          || !newContact.zip.trim()
                          || !newContact.country_code.trim()
                          || createContact.isPending
                        }
                      >
                        {createContact.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Save contact'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {orderType === 'uvwh_intake' && (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-primary">Destination</label>
                <p className="rounded border border-border-subtle bg-surface-sunk px-3 py-2 text-sm text-secondary">
                  UVWH (Uviva Warehouse) — no SPI PO
                </p>
              </div>
            )}

            {phase === 'upload' ? (
              <>
                {/* Shipment sheet drop zone — multi-file */}
                <div onDrop={onDropFiles} onDragOver={onDragOverFiles} className="flex flex-col gap-3">
                  <label className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 transition-colors ${
                    pendingFiles.length > 0
                      ? 'border-success bg-success-subtle/30'
                      : 'border-border bg-surface-sunk hover:border-accent hover:bg-canvas'
                  }`}>
                    <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
                      pendingFiles.length > 0 ? 'bg-success text-on-success' : 'bg-surface border border-border'
                    }`}>
                      {pendingFiles.length > 0
                        ? <Paperclip size={18} />
                        : <Upload size={18} className="text-secondary" />}
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-primary">
                        {pendingFiles.length > 0
                          ? `${pendingFiles.length} file${pendingFiles.length === 1 ? '' : 's'} ready to extract`
                          : 'Drop shipment sheet PDFs here'}
                      </p>
                      <p className="text-xs text-secondary">
                        {pendingFiles.length > 0
                          ? 'Drop more, or click to add'
                          : 'or click to browse — select several at once'}
                      </p>
                    </div>
                    <input
                      ref={shipmentRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      multiple
                      className="sr-only"
                      onChange={(e) => { addFiles(e.target.files); e.target.value = '' }}
                    />
                  </label>

                  {pendingFiles.length > 0 && (
                    <ul className="flex flex-col gap-1.5">
                      {pendingFiles.map((f, idx) => (
                        <li
                          key={`${f.name}-${idx}`}
                          className="flex items-center gap-2 rounded border border-border-subtle bg-surface px-3 py-1.5 text-sm"
                        >
                          <Paperclip size={12} className="text-tertiary" />
                          <span className="flex-1 truncate text-secondary">{f.name}</span>
                          <span className="text-xs text-tertiary">{(f.size / 1024).toFixed(0)} KB</span>
                          <button
                            type="button"
                            onClick={() => removePendingFile(idx)}
                            aria-label={`Remove ${f.name}`}
                            className="text-tertiary hover:text-danger"
                          >
                            <X size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex items-center justify-between pt-2">
                  {entries.length > 0 ? (
                    <Button variant="ghost" size="sm" onClick={() => setPhase('review')}>
                      ← Back to review ({entries.length - submittedCount} pending)
                    </Button>
                  ) : <span />}
                  <Button
                    variant="primary"
                    disabled={pendingFiles.length === 0}
                    onClick={handleExtract}
                  >
                    Extract data →
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-secondary">
                  Reading {queueItems.length} shipment sheet{queueItems.length === 1 ? '' : 's'}…
                </p>
                <ul className="flex flex-col gap-1.5">
                  {queueItems.map((it) => (
                    <li
                      key={it.clientId}
                      className="flex items-center gap-3 rounded border border-border-subtle bg-surface px-3 py-2 text-sm"
                    >
                      {it.status === 'extracting' ? (
                        <Loader2 size={14} className="animate-spin text-accent" />
                      ) : it.status === 'extracted' ? (
                        <CheckCircle2 size={14} className="text-success" />
                      ) : it.status === 'error' ? (
                        <AlertCircle size={14} className="text-danger" />
                      ) : (
                        <FileText size={14} className="text-tertiary" />
                      )}
                      <span className="flex-1 truncate text-secondary">{it.file.name}</span>
                      <Chip
                        size="xs"
                        dot
                        variant={
                          it.status === 'extracted' ? 'success'
                            : it.status === 'error' ? 'danger'
                            : it.status === 'extracting' ? 'info'
                            : 'neutral'
                        }
                      >
                        {it.status === 'extracted' ? 'Extracted'
                          : it.status === 'error' ? 'Error'
                          : it.status === 'extracting' ? 'Extracting…'
                          : 'Queued'}
                      </Chip>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* ── Phase 2: Review ──────────────────────────────────────────────── */}
        {phase === 'review' && (
          entries.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <AlertCircle size={24} className="text-tertiary" />
              <p className="text-sm text-secondary">No shipment sheets extracted successfully yet.</p>
              <Button variant="ghost" size="sm" onClick={() => setPhase('upload')}>← Back to details</Button>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {queueIssues.length > 0 && (
                <div className="flex flex-col gap-1.5 rounded border border-warning-border bg-warning-subtle/40 px-3 py-2">
                  <span className="text-xs font-medium text-secondary">Extraction issues</span>
                  {queueIssues.map((it) => (
                    <div key={it.clientId} className="flex items-center gap-2 text-xs">
                      <span className="flex-1 truncate text-secondary">{it.file.name}</span>
                      {it.status === 'error' ? (
                        <>
                          <span className="max-w-[24ch] truncate text-danger" title={it.errorMessage}>
                            {it.errorMessage}
                          </span>
                          <button
                            type="button"
                            onClick={() => retryItem(it)}
                            className="font-medium text-accent hover:underline"
                          >
                            Retry
                          </button>
                          <button
                            type="button"
                            onClick={() => removeQueueItem(it.clientId)}
                            className="text-tertiary hover:text-danger"
                          >
                            Remove
                          </button>
                        </>
                      ) : (
                        <span className="text-secondary">
                          {it.status === 'extracting' ? 'extracting…' : 'queued'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Stepper */}
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={goPrev} disabled={currentIndex === 0}>
                  <ChevronLeft size={14} /> Prev
                </Button>
                <span className="flex items-center gap-2 text-sm text-secondary">
                  Reviewing {currentIndex + 1} of {entries.length}
                  {current?.status === 'submitted' && <Chip size="xs" variant="success">Submitted</Chip>}
                </span>
                <Button variant="ghost" size="sm" onClick={goNext} disabled={currentIndex === entries.length - 1}>
                  Next <ChevronRight size={14} />
                </Button>
              </div>

              {current && (
                <>
                  {current.form.extraction_notes && (
                    <div className="rounded border border-warning-border bg-warning-subtle/40 px-3 py-2 text-sm text-secondary">
                      <span className="font-medium">Note: </span>{current.form.extraction_notes}
                    </div>
                  )}

                  {/* Order type — shared across the batch, set in step 1 */}
                  <div className="flex items-center justify-between rounded border border-border-subtle bg-surface-sunk px-3 py-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-primary">{ORDER_TYPE_LABELS[orderType]}</span>
                      {orderType === 'uviva_direct' && (
                        <>
                          <span className="text-tertiary">·</span>
                          <span className="text-secondary">
                            {shipToContacts.data?.contacts.find((c) => c.id === shipToId)?.name ?? 'No ship-to selected'}
                          </span>
                        </>
                      )}
                    </div>
                    <span className="truncate text-xs text-tertiary">{current.file.name}</span>
                  </div>

                  {/* Header fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-primary">Shipment Reference *</label>
                      <input
                        type="text"
                        value={current.form.shipment_ref}
                        disabled={current.status !== 'reviewing'}
                        onChange={(e) => setEntryField(current.clientId, 'shipment_ref', e.target.value)}
                        className="rounded border border-border bg-surface px-3 py-2 text-sm text-primary outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
                      />
                    </div>
                    {orderType === 'spi_order' && (
                      <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-primary">PO Reference *</label>
                        <input
                          type="text"
                          value={current.form.po_ref}
                          disabled={current.status !== 'reviewing'}
                          onChange={(e) => setEntryField(current.clientId, 'po_ref', e.target.value)}
                          className="rounded border border-border bg-surface px-3 py-2 text-sm text-primary outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
                        />
                      </div>
                    )}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-primary">Order Reference</label>
                      <input
                        type="text"
                        value={current.form.order_ref}
                        disabled={current.status !== 'reviewing'}
                        onChange={(e) => setEntryField(current.clientId, 'order_ref', e.target.value)}
                        className="rounded border border-border bg-surface px-3 py-2 text-sm text-primary outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
                        placeholder="Optional"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-primary">Planned Delivery</label>
                      <input
                        type="date"
                        value={current.form.planned_delivery_date}
                        disabled={current.status !== 'reviewing'}
                        onChange={(e) => setEntryField(current.clientId, 'planned_delivery_date', e.target.value)}
                        className="rounded border border-border bg-surface px-3 py-2 text-sm text-primary outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
                      />
                    </div>
                  </div>

                  {/* Lines */}
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <h3 className="text-role-section-label">Lines ({current.form.lines.length})</h3>
                      {current.status === 'reviewing' && (
                        <Button variant="ghost" size="sm" onClick={() => addEntryLine(current.clientId)}>+ Add line</Button>
                      )}
                    </div>

                    {current.form.lines.map((ln, idx) => (
                      <div
                        key={idx}
                        className="flex flex-col gap-2 rounded border border-border-subtle bg-surface-sunk p-3"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-primary">Line {idx + 1}</span>
                          {current.status === 'reviewing' && current.form.lines.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeEntryLine(current.clientId, idx)}
                              className="text-tertiary hover:text-danger"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                          <div className="flex flex-col gap-1">
                            <label className="text-xs text-secondary">Product Code *</label>
                            <input
                              type="text"
                              value={ln.product_code}
                              disabled={current.status !== 'reviewing'}
                              onChange={(e) => setEntryLineField(current.clientId, idx, 'product_code', e.target.value)}
                              className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
                            />
                          </div>
                          <div className="col-span-2 flex flex-col gap-1">
                            <label className="text-xs text-secondary">Product Name</label>
                            <input
                              type="text"
                              value={ln.product_name}
                              disabled={current.status !== 'reviewing'}
                              onChange={(e) => setEntryLineField(current.clientId, idx, 'product_name', e.target.value)}
                              className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs text-secondary">Lot / Serial *</label>
                            <input
                              type="text"
                              value={ln.lot_serial}
                              disabled={current.status !== 'reviewing'}
                              onChange={(e) => setEntryLineField(current.clientId, idx, 'lot_serial', e.target.value)}
                              className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs text-secondary">Qty *</label>
                            <input
                              type="number"
                              min="0.001"
                              step="0.001"
                              value={ln.qty_to_ship}
                              disabled={current.status !== 'reviewing'}
                              onChange={(e) => setEntryLineField(current.clientId, idx, 'qty_to_ship', e.target.value)}
                              className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs text-secondary">UoM</label>
                            <input
                              type="text"
                              value={ln.uom}
                              disabled={current.status !== 'reviewing'}
                              onChange={(e) => setEntryLineField(current.clientId, idx, 'uom', e.target.value)}
                              className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs text-secondary">Sell By</label>
                            <input
                              type="date"
                              value={ln.sell_by}
                              disabled={current.status !== 'reviewing'}
                              onChange={(e) => setEntryLineField(current.clientId, idx, 'sell_by', e.target.value)}
                              className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
                            />
                          </div>
                          <div className="col-span-2 flex flex-col gap-1">
                            <label className="text-xs text-secondary">CoA PDF</label>
                            <label className="flex cursor-pointer items-center gap-2 rounded border border-border bg-surface px-2 py-1.5 text-sm text-secondary hover:bg-surface-sunk">
                              <Paperclip size={12} />
                              {ln.coa_file ? (
                                <span className="text-success">{ln.coa_file.name}</span>
                              ) : (
                                <span>Attach CoA…</span>
                              )}
                              <input
                                type="file"
                                accept=".pdf,application/pdf"
                                className="sr-only"
                                disabled={current.status !== 'reviewing'}
                                onChange={(e) => setEntryLineField(current.clientId, idx, 'coa_file', e.target.files?.[0] ?? null)}
                              />
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CoA summary */}
                  <div className="flex items-center gap-3 rounded border border-border-subtle bg-surface-sunk px-4 py-2 text-sm">
                    <span className="text-secondary">Shipment sheet:</span>
                    <Chip variant="success" size="xs">{current.file.name}</Chip>
                    <span className="text-secondary ml-2">CoAs attached:</span>
                    <span className="text-primary font-medium">
                      {current.form.lines.filter((l) => l.coa_file).length} / {current.form.lines.length}
                    </span>
                  </div>

                  {/* Other shipment documents (optional) */}
                  <div className="flex flex-col gap-2">
                    <h3 className="text-role-section-label">Other documents (optional)</h3>
                    <p className="text-xs text-tertiary">
                      Bill of Lading and Invoice can be attached now or added later from the
                      submission's detail view once they're available.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex cursor-pointer items-center gap-2 rounded border border-border bg-surface px-2 py-1.5 text-sm text-secondary hover:bg-surface-sunk">
                        <Paperclip size={12} />
                        {current.billOfLading ? (
                          <span className="text-success">{current.billOfLading.name}</span>
                        ) : (
                          <span>Attach Bill of Lading…</span>
                        )}
                        <input
                          type="file"
                          accept=".pdf,application/pdf"
                          className="sr-only"
                          disabled={current.status !== 'reviewing'}
                          onChange={(e) => updateEntry(current.clientId, { billOfLading: e.target.files?.[0] ?? null })}
                        />
                      </label>
                      <label className="flex cursor-pointer items-center gap-2 rounded border border-border bg-surface px-2 py-1.5 text-sm text-secondary hover:bg-surface-sunk">
                        <Paperclip size={12} />
                        {current.invoice ? (
                          <span className="text-success">{current.invoice.name}</span>
                        ) : (
                          <span>Attach Invoice…</span>
                        )}
                        <input
                          type="file"
                          accept=".pdf,application/pdf"
                          className="sr-only"
                          disabled={current.status !== 'reviewing'}
                          onChange={(e) => updateEntry(current.clientId, { invoice: e.target.files?.[0] ?? null })}
                        />
                      </label>
                    </div>
                  </div>

                  {current.submitError && (
                    <p className="rounded border border-danger-border bg-danger-subtle px-3 py-2 text-sm text-danger">
                      {current.submitError}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setPhase('upload')}>
                        ← Add more / edit details
                      </Button>
                      {current.status !== 'submitted' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (window.confirm('Remove this shipment sheet from the batch? Nothing will be submitted for it.')) {
                              removeEntry(current.clientId)
                            }
                          }}
                        >
                          Remove from batch
                        </Button>
                      )}
                    </div>
                    {current.status === 'submitted' ? (
                      <Chip variant="success" size="md">Submitted ✓</Chip>
                    ) : (
                      <Button
                        variant="primary"
                        onClick={() => handleSubmitEntry(current)}
                        disabled={current.status === 'submitting' || !isEntryValid(current)}
                      >
                        {current.status === 'submitting' ? (
                          <><Loader2 size={14} className="animate-spin" /> Submitting…</>
                        ) : (
                          'Submit Shipment →'
                        )}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          )
        )}
      </div>
    </Drawer>
  )
}
